import type { SnapshotMeta } from '@/document/model'
import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { keepCanvasRequest } from '@/play/canvasRequest'
import type { Position } from '@/voxelMath'
import { loadPage } from '@/play/pageRegistry'

/**
 * Whether a saved world is "empty" and can be tidied away (owner, 2026-10-15: empty worlds should not
 * pile up). A plain world is empty when it has no voxels. A page world starts filled with its page's
 * seed, so it is empty when its voxels are still exactly that seed (nothing kept since the page made
 * it; edits that were all undone count as nothing). Page definitions are never touched; a world from
 * an older page version (a different seed now) is kept. A page world loads its page's body (to make
 * the seed) if it is not loaded yet.
 */
export async function isEmptyWorld(base: VoxelMap, meta: SnapshotMeta): Promise<boolean> {
  const experience = (meta as { experience?: { id?: string; version?: number } }).experience
  if (!experience?.id) return base.size === 0
  const seed = await pageSeedBase(experience.id, experience.version, meta.bounds)
  return seed !== null && sameVoxels(base, seed)
}

const seedCache = new Map<string, VoxelMap | null>()

// The seed is made in the world's own bounds (a device-shaped page made it for its screen).
async function pageSeedBase(id: string, version: number | undefined, bounds: Position): Promise<VoxelMap | null> {
  const key = `${id}@${version ?? ''}@${bounds.x}x${bounds.y}x${bounds.z}`
  if (seedCache.has(key)) return seedCache.get(key) ?? null
  let seed: VoxelMap | null = null
  const page = await loadPage(id)
  if (page) {
    try {
      const made = page.createDocument(keepCanvasRequest(bounds))
      const madeVersion = (made.meta as { experience?: { version?: number } }).experience?.version
      // A world from another version of the page had another seed: never judge it by this one.
      if (version === undefined || madeVersion === version) seed = made.base
    } catch {
      seed = null
    }
  }
  seedCache.set(key, seed)
  return seed
}

function sameVoxels(a: VoxelMap, b: VoxelMap): boolean {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const [key, value] of a.entries()) {
    const other = b.get(key)
    if (!other || !sameVoxel(value, other)) return false
  }
  return true
}

function sameVoxel(a: VoxelData, b: VoxelData): boolean {
  return a.type === b.type
    && (a.color ?? null) === (b.color ?? null)
    && (a.face ?? null) === (b.face ?? null)
    && JSON.stringify(a.material ?? null) === JSON.stringify(b.material ?? null)
}
